import { Link } from "react-router-dom";
import { WaitlistForm } from "@/components/platform/WaitlistForm";
import { ArrowRight, GraduationCap, Wrench, Users, PlayCircle, Clock3, Sparkles } from "lucide-react";
import { BtnLink, Eyebrow, btnClass } from "@/components/kit";
import { ProductGrid } from "@/components/platform/Products";
import { PLATFORM } from "@/config/platform";

const PILLARS = [
  { Icon: PlayCircle, t: "Apprendre à créer", d: "Un parcours guidé pour réaliser ta première vidéo UGC, de l'idée à l'export.", status: "UGC GO · disponible", live: true },
  { Icon: Wrench, t: "Gagner du temps", d: "Des guides, scripts, modèles et checklists prêts à l'emploi.", status: "Boîte à outils · bientôt" },
  { Icon: Sparkles, t: "Gérer tes collaborations", d: "Une application dédiée à l'organisation de tes collaborations.", status: "Collaby · disponible" },
  { Icon: Users, t: "Avancer ensemble", d: "Un cercle de créatrices pour échanger et se motiver.", status: "Le Cercle · bientôt" },
];

const SPACES = [
  { to: "/boite-a-outils", Icon: Wrench, t: "Boîte à outils", d: "Guides, PDF, scripts, prompts, modèles et checklists." },
  { to: "/le-cercle", Icon: Users, t: "Le Cercle UGC & CO", d: "L'espace communautaire des créatrices UGC." },
  { to: "/academie", Icon: GraduationCap, t: "Académie", d: "Formations et tutoriels pour aller plus loin." },
];

function Hero() {
  return (
    <section className="relative overflow-hidden" data-testid="platform-hero">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full border border-gold/20" aria-hidden />
      <div className="pointer-events-none absolute -right-20 -top-20 h-[380px] w-[380px] rounded-full border border-gold/10" aria-hidden />
      <div className="mx-auto grid max-w-6xl items-center gap-14 px-5 pb-20 pt-12 sm:px-8 md:pt-20 lg:grid-cols-[1.2fr_1fr] lg:pb-28">
        <div className="stagger">
          <Eyebrow>L'univers des créatrices de contenu</Eyebrow>
          <h1 className="mt-6 text-[clamp(2.1rem,11vw,3rem)] font-semibold leading-[1.05] tracking-tight text-bx sm:text-6xl lg:text-7xl" data-testid="platform-hero-title">
            <span className="block">Tout pour créer.</span>
            <span className="block">Tout pour <em className="font-medium italic text-gold-deep">évoluer</em><span className="text-gold">.</span></span>
          </h1>
          <span className="mt-7 block h-px w-24 bg-gradient-to-r from-gold to-transparent" aria-hidden />
          <div className="mt-7 max-w-xl space-y-3 text-lg leading-relaxed text-ink/80" data-testid="platform-hero-subtitle">
            <p>Un seul univers pour créer, t'organiser, développer ton activité et te connecter à d'autres créatrices.</p>
            <p className="text-base text-soft sm:text-lg">Découvre des outils, des applications et des ressources pensés pour faire avancer ton business à ton rythme.</p>
          </div>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <BtnLink to="/applications" size="lg" className="group whitespace-nowrap" data-testid="platform-hero-explore">
              Explorer les outils<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </BtnLink>
            <a href="#avantages" className={`${btnClass("outline", "lg")} whitespace-nowrap`} data-testid="platform-hero-discover">Découvrir UGC & CO</a>
          </div>
          <p className="mt-6 flex items-center gap-2 text-sm text-soft" data-testid="platform-hero-reassurance">
            <Sparkles className="h-4 w-4 shrink-0 text-gold" strokeWidth={1.6} />Des outils pour chaque étape de ton parcours de créatrice.
          </p>
        </div>
        <div className="relative mx-auto w-full max-w-md animate-fade-up">
          <div className="absolute -inset-3 rounded-[32px] border border-gold/40" aria-hidden />
          <div className="relative rounded-[28px] bg-white p-8 shadow-[0_30px_60px_-30px_rgba(101,31,56,0.35)]">
            <img src={PLATFORM.logoFull} alt={`${PLATFORM.name} — ${PLATFORM.tagline}`} className="w-full" data-testid="platform-hero-logo" />
            <div className="mt-6 flex flex-wrap justify-center gap-2 border-t border-line pt-6" aria-label="Les espaces UGC & CO">
              {["Applications", "Ressources", "Communauté"].map((t) => (
                <span key={t} className="rounded-full border border-gold/40 bg-ivory px-3.5 py-1.5 text-xs font-medium tracking-wide text-bx">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pillars() {
  return (
    <section id="avantages" className="scroll-mt-20 bg-white">
      <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 md:py-24">
        <Eyebrow>Ce que UGC & CO t'apporte</Eyebrow>
        <h2 className="mt-4 max-w-2xl text-3xl font-semibold text-bx sm:text-4xl">Tout ce qu'il faut pour créer, t'organiser et faire grandir ton activité.</h2>
        <div className="mt-12 grid gap-px overflow-hidden rounded-[22px] border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
          {PILLARS.map(({ Icon, t, d, status, live }) => (
            <div key={t} className="bg-white p-7" data-testid={`pillar-${t}`}>
              <Icon className="h-6 w-6 text-bx" strokeWidth={1.4} />
              <h3 className="mt-5 font-display text-xl font-semibold text-ink">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-soft">{d}</p>
              <p className={`mt-5 inline-flex items-center gap-1.5 text-xs font-semibold ${live ? "text-bx" : "text-bx-2/80"}`}>
                {live ? <Sparkles className="h-3.5 w-3.5 text-gold" /> : <Clock3 className="h-3.5 w-3.5 text-gold" />}{status}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Apps() {
  return (
    <section className="mx-auto max-w-6xl px-5 py-20 sm:px-8 md:py-24">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <Eyebrow>Les applications</Eyebrow>
          <h2 className="mt-4 text-3xl font-semibold text-bx sm:text-4xl">Des outils pensés pour chaque étape.</h2>
        </div>
        <Link to="/applications" className="inline-flex items-center gap-2 text-sm font-semibold text-bx hover:underline" data-testid="home-all-apps">Tout le catalogue<ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-10"><ProductGrid testId="home-products" /></div>
    </section>
  );
}

function Spaces() {
  return (
    <section className="mx-auto max-w-6xl px-5 pb-20 sm:px-8 md:pb-24">
      <Eyebrow>Bientôt sur UGC & CO</Eyebrow>
      <h2 className="mt-4 text-3xl font-semibold text-bx sm:text-4xl">Ressources, communauté et formations.</h2>
      <div className="mt-10 grid gap-5 md:grid-cols-3">
        {SPACES.map(({ to, Icon, t, d }) => (
          <Link key={to} to={to} data-testid={`home-space-${to.slice(1)}`}
            className="group rounded-[22px] border border-line bg-white p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-bx/30">
            <span className="flex h-12 w-12 items-center justify-center rounded-full ring-1 ring-gold/60"><Icon className="h-5 w-5 text-bx" strokeWidth={1.5} /></span>
            <h3 className="mt-5 font-display text-xl font-semibold text-ink group-hover:text-bx">{t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-soft">{d}</p>
            <p className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-bx-2"><Clock3 className="h-3.5 w-3.5 text-gold" />Bientôt disponible</p>
          </Link>
        ))}
      </div>
      <div className="mt-8 max-w-3xl"><WaitlistForm testId="home-waitlist" /></div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="mx-auto max-w-6xl px-5 pb-24 sm:px-8">
      <div className="grain relative overflow-hidden rounded-[28px] bg-[#161215] px-6 py-14 text-white sm:px-14">
        <div className="relative z-10 flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Commence aujourd'hui</p>
            <h2 className="mt-4 max-w-lg text-3xl font-semibold sm:text-4xl">Ta première vidéo UGC, pas à pas.</h2>
            <p className="mt-3 text-white/70">UGC GO · 7 missions · 9 € en paiement unique</p>
          </div>
          <BtnLink to="/ugc-go" variant="light" size="lg" className="group whitespace-nowrap" data-testid="platform-final-cta">
            Découvrir UGC GO<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </BtnLink>
        </div>
      </div>
    </section>
  );
}

export default function PlatformHome() {
  return (
    <div data-testid="platform-home">
      <Hero />
      <Pillars />
      <Apps />
      <Spaces />
      <FinalCta />
    </div>
  );
}
